import React, { useState, useMemo } from 'react'

import { Plus, Edit2, Trash2, Search, User } from 'lucide-react'
import { useCRMStore } from '../store/crmStore'

export default function ContactsPage() {
  const contacts = useCRMStore((state) => state.contacts)
  const customers = useCRMStore((state) => state.customers)
  const addContact = useCRMStore((state) => state.addContact)
  const updateContact = useCRMStore((state) => state.updateContact)
  const deleteContact = useCRMStore((state) => state.deleteContact)

  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [selectedContact, setSelectedContact] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    customerId: 1,
  })

  const filteredContacts = useMemo(() => {
    return contacts.filter(contact =>
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [contacts, searchTerm])

  const handleOpenModal = (contact = null) => {
    if (contact) {
      setSelectedContact(contact)
      setFormData(contact)
    } else {
      setSelectedContact(null)
      setFormData({
        name: '',
        title: '',
        company: '',
        email: '',
        phone: '',
        customerId: 1,
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedContact(null)
    setFormData({
      name: '',
      title: '',
      company: '',
      email: '',
      phone: '',
      customerId: 1,
    })
  }

  const handleSave = () => {
    if (!formData.name || !formData.email || !formData.title) {
      alert('Please fill in all required fields')
      return
    }

    if (selectedContact) {
      updateContact(selectedContact.id, formData)
    } else {
      addContact(formData)
    }
    handleCloseModal()
  }

  const handleDelete = (contactId) => {
    if (confirm('Are you sure you want to delete this contact?')) {
      deleteContact(contactId)
    }
  }

  const getCustomerName = (customerId) => {
    return customers.find(c => c.id === customerId)?.name || 'Unknown'
  }

  const getTitleColor = (title) => {
    const upperTitle = title.toUpperCase()
    if (upperTitle.includes('CEO') || upperTitle.includes('CTO') || upperTitle.includes('VP') || upperTitle.includes('DIRECTOR')) {
      return 'bg-red-500/20 text-red-300'
    }
    if (upperTitle.includes('MANAGER')) {
      return 'bg-yellow-500/20 text-yellow-300'
    }
    return 'bg-blue-500/20 text-blue-300'
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Contacts</h1>
              <p className="text-slate-400">Manage customer contacts and decision makers</p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all"
            >
              <Plus size={20} />
              Add Contact
            </button>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Total Contacts</p>
              <p className="text-3xl font-bold text-white">{contacts.length}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Companies</p>
              <p className="text-3xl font-bold text-blue-400">{new Set(contacts.map(c => c.company)).size}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Executives</p>
              <p className="text-3xl font-bold text-red-400">
                {contacts.filter(c => c.title.toUpperCase().includes('VP') || c.title.toUpperCase().includes('CEO') || c.title.toUpperCase().includes('CTO') || c.title.toUpperCase().includes('DIRECTOR')).length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Email Contacts</p>
              <p className="text-3xl font-bold text-green-400">{contacts.filter(c => c.email).length}</p>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search contacts by name, title, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-400/50"
              />
            </div>
          </div>

          {/* Contacts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContacts.length > 0 ? (
              filteredContacts.map((contact) => (
                <div key={contact.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg bg-blue-500/20">
                      <User size={24} className="text-blue-400" />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenModal(contact)}
                        className="p-2 hover:bg-slate-700 rounded transition-colors text-blue-400"
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="p-2 hover:bg-slate-700 rounded transition-colors text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-1">{contact.name}</h3>
                  <div className="space-y-2 mb-4">
                    <div>
                      <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getTitleColor(contact.title)}`}>
                        {contact.title}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400">{contact.company}</p>
                    <div className="text-sm text-slate-500">
                      <p className="text-blue-300 hover:text-blue-200 cursor-pointer">{contact.email}</p>
                      <p className="text-slate-400">{contact.phone}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-blue-500/10">
                    <p className="text-xs text-slate-400">
                      Customer: <span className="text-blue-300">{getCustomerName(contact.customerId)}</span>
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-slate-400 text-lg">No contacts found</p>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Contact Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center p-6 border-b border-blue-500/20">
                <h2 className="text-2xl font-bold text-white">
                  {selectedContact ? 'Edit Contact' : 'Add New Contact'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                  <input
                    type="text"
                    placeholder="Title *"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                  <select
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: parseInt(e.target.value) })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-400"
                  >
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-4 p-6 border-t border-blue-500/20">
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-2 rounded border border-slate-600 text-slate-300 hover:border-slate-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded transition-all"
                >
                  Save Contact
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  )
}
